import { Stack } from "@mui/material";
import CustomizedButton from "components/Custom/CustomizedButton";
import CustomerInfo from "components/Form/Logistic/CustomerInfo";
import DailyWorksheet from "components/Form/Logistic/DeliveryTrip/DailyWorksheet";
import DateDistanceInfo from "components/Form/Logistic/DeliveryTrip/DateDistanceInfo";
import DeliveryInfo from "components/Form/Logistic/DeliveryTrip/DeliveryInfo";
import FuelInfo from "components/Form/Logistic/DeliveryTrip/FuelInfo";
import DeliveryTripHeader from "components/Form/Logistic/DeliveryTrip/Header";
import DeliveryRemark from "components/Form/Logistic/DeliveryTrip/Remark";
import DeliveryTripVehicle from "components/Form/Logistic/DeliveryTrip/Vehicle";
import ExpensesList from "components/Form/Logistic/ExpensesList";
import IncomeList from "components/Form/Logistic/IncomeList";
import WorkList from "components/Form/Logistic/WorkList";
import BottomNavbar from "components/UI/Navbar/BottomNavbar";
import dayjs from "dayjs";
import { ActivityLogDocumentType, ActivityType } from "generated/general";
import {
  DeliveryTripCreateInput,
  DeliveryTripUpdateInput,
  useDeliveryTripCreateMutation,
  useDeliveryTripFindUniqueQuery,
  useDeliveryTripUpdateMutation,
} from "generated/sales";
import { useActivityLog } from "hooks/use-activity-log";
import { useDisable } from "hooks/use-disable";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStateContext } from "contexts/auth-context";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import {
  deliveryTripCreatePayload,
  deliveryTripDataFormatter,
  deliveryTripUpdatePayload,
  workListQueryFormatter,
} from "utils/Formatter/Logistic/DeliveryTrip";
import { errorMessageFormatter } from "utils/Global";

import LoadingUI from "components/UI/LoadingUI";
import AddressInfo from "components/Form/Logistic/CustomerInfo/AddressInfo";
import LoadingAnimation from "components/Animation/Loading";
import DateOfPayment from "components/Form/Logistic/DeliveryTrip/DateOfPayment";
import { useBillingNoteStatus } from "hooks/Logistic/DeliveryTrip/use-delivery-trip-check-billing-note";
import { incomeItemsConfig } from "components/Form/Logistic/DeliveryTrip/schema";
import { ITEM_VIEW } from "services/AgGrid/ItemAggrid";
import { IIncomeList } from "types/Logistic";
import { v4 as uuidv4 } from "uuid";

const DocumentInfoTab = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
  } = useFormContext<IDeliveryTrip>();
  const {
    state: { permissions },
  } = useStateContext();

  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { createActivityLog } = useActivityLog();

  const [, setDisabled] = useDisable();

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const graphqlClient = createGraphQLClientWithMiddleware("sales");

  // Fetch item master data once on new document creation
  useEffect(() => {
    if (id || state) return;

    const ALL_ITEM_UIDS = [
      "SP00001",
      "SP00003",
      "SP00005",
      "SP00006",
      "SP00016",
      "SP00009",
    ];

    (async () => {
      try {
        const graphqlClientWms = createGraphQLClientWithMiddleware("wms");
        const { ItemsViewAggrid } = await graphqlClientWms.request(ITEM_VIEW, {
          aggridInput: {
            startRow: 0,
            endRow: ALL_ITEM_UIDS.length,
            filterModel: {
              unique_id: { filterType: "set", values: ALL_ITEM_UIDS },
            },
            sortModel: [],
          },
        });

        const map: Record<string, any> = {};
        for (const item of ItemsViewAggrid.results) {
          map[item.unique_id] = item;
        }

        const incomeList: IIncomeList[] = incomeItemsConfig
          .filter(({ uid }) => map[uid])
          .map(({ uid, withholding_tax_type, ...rest }) => {
            const item = map[uid];
            return {
              unique_id: uuidv4(),
              item_id: item.id || 0,
              item_unique_id: item.unique_id || "",
              item_name: item.name || "",
              item_sku_desc: item.description,
              qty: 1,
              uom_id: item.stock_uom_id || 0,
              uom_name: item.stock_uom_name || "",
              uom_conversion_list: item.uom_conversion_list,
              discount: 0,
              pre_vat_amount: 0,
              price_per_unit: 0,
              withholding_tax_type,
              withholding_tax_value: 0,
              vat_percentage: "ไม่มี",
              ...rest,
            };
          });
        setValue("income_list", incomeList);
      } catch (_) {
        // silently fall back — income_list stays empty from schema default
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, refetch } = useDeliveryTripFindUniqueQuery(
    graphqlClient,
    {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    },
    {
      enabled: !!id,
    },
  );

  useEffect(() => {
    if (state) {
      const {
        work_list,
        created_date,
        issue_date,
        plan_start_date,
        plan_end_date,
        copied_unique_id,
        copied_id,
        ...otherState
      } = state;
      const formatWorkList = work_list.map(
        ({ start_date, end_date, ...work }: any) => ({
          ...work,
          start_date: start_date ? dayjs(start_date) : undefined,
          end_date: end_date ? dayjs(end_date) : undefined,
        }),
      );
      const formatState = {
        ...otherState,
        created_date: dayjs(created_date),
        issue_date: dayjs(issue_date),
        plan_start_date: dayjs(plan_start_date),
        plan_end_date: dayjs(plan_end_date),
        work_list: formatWorkList,
      };
      reset(formatState);
    }
  }, [reset, state]);

  // 86d4b1jdw — the expenses seeding that used to sit here moved to
  // `ExpensesList` (via `useExpensePresets`). It only ran at mount, so a driver
  // picked afterwards — the normal flow now — got an empty section, and a
  // saved document never seeded at all. The reconciler there seeds per driver
  // whenever a driver has no rows.

  const mainStatus = watch("main_status");
  const aggrid_status = watch("aggrid_status");
  const flagStatus = watch("flag_status");

  const late = flagStatus.includes("late");
  const cancelled = flagStatus.includes("cancelled");

  const status = late && !cancelled ? mainStatus : aggrid_status;

  const [isEdit, setIsEdit] = useState<boolean>(false);

  // Any saved document opens in View Mode; `ตัวเลือก > แก้ไข` enters Edit.
  // (Was `status !== "draft"` — draft is now dead.)
  useEffect(() => {
    if (id) {
      setDisabled(true);
    }
    return () => {
      setDisabled(false);
    };
  }, [setDisabled, id]);

  const { mutateAsync: create, isLoading: isCreating } =
    useDeliveryTripCreateMutation<Error>(graphqlClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    useDeliveryTripUpdateMutation<Error>(graphqlClient);

  // Single save handler. The dropdown writes to `new_main_status`; on save
  // we forward that as the payload status. `main_status` on the form stays
  // frozen at the saved value so the badge doesn't flicker mid-edit.
  const saveHandler = async (data: IDeliveryTrip) => {
    const nextStatus = data.new_main_status || data.main_status;
    try {
      if (!id) {
        const formatPayload = deliveryTripCreatePayload(data, nextStatus);
        const { DeliveryTripCreate } = await create({
          createInput: formatPayload as DeliveryTripCreateInput,
        });
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.DeliveryTrip,
            reference_id: DeliveryTripCreate?.id || 0,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: DeliveryTripCreate?.id,
                unique_id: DeliveryTripCreate?.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.DeliveryTrip,
          reference_id: DeliveryTripCreate?.id || 0,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: nextStatus,
          },
        });
        navigate(`/logistic/delivery-trip/${DeliveryTripCreate?.id}`);
      } else {
        // Compare against the frozen `main_status` (saved value) rather than
        // `aggrid_status` — the latter can be "late" while main_status is
        // "wait_deliver", producing spurious StatusChange logs.
        const prevStatus = data.main_status;
        const formatPayload = await deliveryTripUpdatePayload(data, nextStatus);
        const { DeliveryTripUpdate } = await update({
          uniqueInput: {
            id: parseInt(id),
          },
          updateInput: formatPayload as DeliveryTripUpdateInput,
        });
        // ActivityLog: emit StatusChange only when main_status actually changed;
        // Edit-only saves get an Edit entry.
        const statusChanged = prevStatus !== nextStatus;
        if (statusChanged) {
          await createActivityLog({
            activity_type: ActivityType.StatusChange,
            document_type: ActivityLogDocumentType.DeliveryTrip,
            reference_id: DeliveryTripUpdate?.id || 0,
            activity_detail: {
              prev_status: prevStatus,
              curr_status: nextStatus,
            },
          });
        } else if (isDirty) {
          await createActivityLog({
            activity_type: ActivityType.Edit,
            document_type: ActivityLogDocumentType.DeliveryTrip,
            reference_id: DeliveryTripUpdate?.id || 0,
            activity_detail: {
              secondary_operation: ActivityType.Edit,
            },
          });
        }
      }
      enqueueSnackbar(`${t("button.save")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();
      setIsEdit(false);
      if (id) setDisabled(true);
    } catch (err) {
      const formatError = errorMessageFormatter(err, "document");
      enqueueSnackbar(formatError || `${t("button.save")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const editClickHandler = () => {
    setDisabled(false);
    setIsEdit(true);
  };

  const cancelEditHandler = () => {
    setIsEdit(false);
    setDisabled(true);

    const formatData = deliveryTripDataFormatter(
      data?.DeliveryTripFindUnique,
    ) as IDeliveryTrip;

    reset(formatData);
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];
      const formatPayload = await deliveryTripUpdatePayload(
        data,
        data.main_status,
      );
      const { DeliveryTripUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        updateInput: {
          ...formatPayload,
          flag_status,
        } as DeliveryTripUpdateInput,
      });

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.DeliveryTrip,
        reference_id: DeliveryTripUpdate?.id || 0,
        activity_detail: {
          secondary_operation: ActivityType.Edit,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
        },
      });

      enqueueSnackbar(
        `${t("button.cancel")}${t("logistic.delivery_trip.index")}สำเร็จ`,
        {
          variant: "success",
        },
      );

      await refetch();
    } catch (err) {
      enqueueSnackbar(
        `${t("button.cancel")}${t("logistic.delivery_trip.index")}ไม่สำเร็จ`,
        {
          variant: "error",
        },
      );
    }
  };

  const updateLocationData = async () => {
    const currentWorkList = getValues("work_list");
    const vehicleType = getValues("vehicle.type");

    setIsFetching(true);
    const newWorkList = await workListQueryFormatter(
      currentWorkList,
      vehicleType,
    );
    setTimeout(() => {
      setIsFetching(false);
    }, 2000);

    return newWorkList;
  };

  // Footer buttons collapse to a single ยกเลิก / บันทึก pair. Status is a
  // form field now; saveHandler carries whatever main_status is selected.
  const renderButton = () => {
    // Cancelled documents are read-only — no footer buttons.
    if (flagStatus?.includes("cancelled")) {
      return null;
    }
    // New document — plain บันทึก (create).
    if (!id) {
      if (!permissions?.delivery_trip?.create) return null;
      return (
        <CustomizedButton
          variant="contained"
          title={t("button.save")}
          disabled={isMutating}
          onClick={handleSubmit(saveHandler)}
        />
      );
    }
    // Existing document in Edit mode — ยกเลิก / บันทึก.
    if (isEdit) {
      if (!permissions?.delivery_trip?.update) return null;
      return (
        <>
          <CustomizedButton
            variant="outlined"
            title={t("button.cancel")}
            onClick={cancelEditHandler}
            disabled={isUpdating}
          />
          <CustomizedButton
            title={t("button.save")}
            variant="contained"
            onClick={handleSubmit(saveHandler)}
            disabled={isUpdating}
          />
        </>
      );
    }
    // Existing document in View mode — no footer buttons (Options > แก้ไข
    // enters Edit).
    return null;
  };
  const uniqueId = watch("unique_id");
  const isCancellable = useBillingNoteStatus(uniqueId, status);

  // 86d37db3t — customer/address sections are freely editable in every
  // non-cancelled status. `disabled` (View mode) is the only gate now.
  const disabledStatus = flagStatus?.includes("cancelled") ?? false;

  const isMutating = isCreating || isUpdating;

  if (isFetching) {
    return <LoadingAnimation />;
  }

  if (id && isMutating) {
    return <LoadingUI />;
  }

  return (
    <>
      <DeliveryTripHeader
        editClickHandler={editClickHandler}
        cancelHandler={cancelHandler}
        isEdit={isEdit}
        refetch={refetch}
        isCancellable={isCancellable}
      />
      <DeliveryTripVehicle />
      <CustomerInfo forceDisabled={disabledStatus} />
      <AddressInfo forceDisabled={disabledStatus} />
      <DeliveryInfo />
      <DateDistanceInfo />
      <WorkList updateLocationData={updateLocationData} />
      <IncomeList isCancellable={isCancellable} />
      <FuelInfo />
      <ExpensesList />
      <DailyWorksheet />
      <DateOfPayment />
      <DeliveryRemark />
      <BottomNavbar>
        <Stack direction="row" spacing={1} alignItems="center">
          {renderButton()}
        </Stack>
      </BottomNavbar>
    </>
  );
};

export default DocumentInfoTab;
